import type {
  ModifiedPlayer,
  NFLPlayerInputs,
  RemovedPlayer,
  RequestSimulationRunRequest
} from '@library/types/simulations';
import type { LeagueTeamPlayer } from '@library/types/player';
import { PATHS } from '@bo-constants/routes';
import type { SimulationBuilder } from '@bo-hooks/simulations/useSimulationBuilder';
import { useNavigate } from 'react-router-dom';
import { useRequestSimulationRunMutation } from '@bo-services/simulationsApi';

const getPlayerInputs = ({
  defaultPlayers,
  modifiedPlayers,
  player
}: {
  defaultPlayers: Array<ModifiedPlayer>;
  modifiedPlayers: Array<ModifiedPlayer>;
  player: LeagueTeamPlayer;
}): NFLPlayerInputs => {
  const normalizedPlayerId = String(player.foreign_id);

  const modifiedPlayer = modifiedPlayers.find(
    (modPlayer) => modPlayer.foreignId === normalizedPlayerId
  );

  const defaultPlayer = defaultPlayers.find(
    (modPlayer) => modPlayer.foreignId === normalizedPlayerId
  );

  return {
    prizepicks_player_id: normalizedPlayerId,
    sportradar_player_id: normalizedPlayerId,
    player_name: player.name,
    pass_attempt_share:
      (modifiedPlayer?.playerOverrides?.pass_attempt_share ||
        defaultPlayer?.playerOverrides?.pass_attempt_share ||
        0) / 100,
    is_qb:
      modifiedPlayer?.playerOverrides?.is_qb ??
      defaultPlayer?.playerOverrides?.is_qb ??
      false,
    carry_share:
      (modifiedPlayer?.playerOverrides?.carry_share ||
        defaultPlayer?.playerOverrides?.carry_share ||
        0) / 100,
    catch_adjustment:
      (modifiedPlayer?.playerOverrides?.catch_adjustment ||
        defaultPlayer?.playerOverrides?.catch_adjustment ||
        0) / 100,
    yards_per_carry:
      modifiedPlayer?.playerOverrides?.yards_per_carry ||
      defaultPlayer?.playerOverrides?.yards_per_carry ||
      0.0,
    short_yardage_carry_share:
      (modifiedPlayer?.playerOverrides?.short_yardage_carry_share ||
        defaultPlayer?.playerOverrides?.short_yardage_carry_share ||
        0) / 100,
    rush_td_rate:
      (modifiedPlayer?.playerOverrides?.rush_td_rate ||
        defaultPlayer?.playerOverrides?.rush_td_rate ||
        0) / 100,
    rush_td_adjustment:
      (modifiedPlayer?.playerOverrides?.rush_td_adjustment ||
        defaultPlayer?.playerOverrides?.rush_td_adjustment ||
        0) / 100,
    target_rate:
      (modifiedPlayer?.playerOverrides?.target_rate ||
        defaultPlayer?.playerOverrides?.target_rate ||
        0) / 100,
    target_adjustment:
      (modifiedPlayer?.playerOverrides?.target_adjustment ||
        defaultPlayer?.playerOverrides?.target_adjustment ||
        0) / 100,
    average_depth_of_target:
      modifiedPlayer?.playerOverrides?.average_depth_of_target ||
      defaultPlayer?.playerOverrides?.average_depth_of_target ||
      0.0,
    yards_per_target:
      modifiedPlayer?.playerOverrides?.yards_per_target ||
      defaultPlayer?.playerOverrides?.yards_per_target ||
      0.0,
    yards_per_completion:
      modifiedPlayer?.playerOverrides?.yards_per_completion ||
      defaultPlayer?.playerOverrides?.yards_per_completion ||
      0.0,
    catch_rate:
      (modifiedPlayer?.playerOverrides?.catch_rate ||
        defaultPlayer?.playerOverrides?.catch_rate ||
        0) / 100,
    rec_td_rate:
      (modifiedPlayer?.playerOverrides?.rec_td_rate ||
        defaultPlayer?.playerOverrides?.rec_td_rate ||
        0) / 100,
    rec_td_adjustment:
      (modifiedPlayer?.playerOverrides?.rec_td_adjustment ||
        defaultPlayer?.playerOverrides?.rec_td_adjustment ||
        0) / 100,
    pass_play_playing_time:
      (modifiedPlayer?.playerOverrides?.pass_play_playing_time ||
        defaultPlayer?.playerOverrides?.pass_play_playing_time ||
        0) / 100,
    rush_play_playing_time:
      (modifiedPlayer?.playerOverrides?.rush_play_playing_time ||
        defaultPlayer?.playerOverrides?.rush_play_playing_time ||
        0) / 100,
    pass_play_blowout_playing_time:
      (modifiedPlayer?.playerOverrides?.pass_play_blowout_playing_time ||
        defaultPlayer?.playerOverrides?.pass_play_blowout_playing_time ||
        0) / 100,
    rush_play_blowout_playing_time:
      (modifiedPlayer?.playerOverrides?.rush_play_blowout_playing_time ||
        defaultPlayer?.playerOverrides?.rush_play_blowout_playing_time ||
        0) / 100,
    route_run_rate:
      (modifiedPlayer?.playerOverrides?.route_run_rate ||
        defaultPlayer?.playerOverrides?.route_run_rate ||
        0) / 100
  };
};

export const useRequestSimulation = ({
  gameId,
  leagueId,
  simulationBuilder
}: {
  gameId: string;
  leagueId: string;
  simulationBuilder: SimulationBuilder;
}) => {
  const navigate = useNavigate();
  const {
    scenarioName,
    scenarioNameDefault,
    gameOverrides,
    homeTeamInputs,
    awayTeamInputs,
    homeTeamPlayers,
    awayTeamPlayers,
    modifiedPlayers,
    removedPlayers,
    defaultPlayers
  } = simulationBuilder;
  const [
    requestSimulationRun
    // { isError, isFetching, isLoading }
  ] = useRequestSimulationRunMutation();
  const handleRequestSimulationRun = async () => {
    const homeTeamRemovedPlayers: RemovedPlayer[] = [];
    const awayTeamRemovedPlayers: RemovedPlayer[] = [];

    removedPlayers.forEach(({ homeTeam, playerId, playerName }) => {
      if (homeTeam) {
        homeTeamRemovedPlayers.push({
          prizepicks_player_id: playerId,
          sportradar_player_id: playerId,
          player_name: playerName
        });
      } else {
        awayTeamRemovedPlayers.push({
          prizepicks_player_id: playerId,
          sportradar_player_id: playerId,
          player_name: playerName
        });
      }
    });

    const homeTeamFinalPlayers = homeTeamPlayers.map((player) =>
      getPlayerInputs({ modifiedPlayers, player, defaultPlayers })
    );

    const awayTeamFinalPlayers = awayTeamPlayers.map((player) =>
      getPlayerInputs({ modifiedPlayers, player, defaultPlayers })
    );

    const payload: RequestSimulationRunRequest = {
      game_id: gameId,
      league_id: leagueId,
      simulation_run_name:
        scenarioName.length > 0 ? scenarioName : scenarioNameDefault,
      simulation_run_inputs: {
        game_inputs: {
          temperature: Number(gameOverrides.temperature),
          wind_speed: Number(gameOverrides.wind_speed),
          grass_field: gameOverrides.grass_field,
          dome_weather: gameOverrides.dome_weather,
          total_points: Number(gameOverrides.total_points),
          home_team_spread: Number(gameOverrides.home_team_spread)
        },
        team_inputs: [
          {
            prizepicks_team_id: homeTeamInputs.prizepicks_team_id,
            sportradar_team_id: homeTeamInputs.sportradar_team_id,
            team_name: homeTeamInputs.team_name,
            home_team: homeTeamInputs.home_team,
            yards_per_pass_attempt: homeTeamInputs.yards_per_pass_attempt,
            yards_per_rush_attempt: homeTeamInputs.yards_per_rush_attempt,
            pass_attempt_rate: homeTeamInputs.pass_attempt_rate / 100,
            pass_completion_prob_adjustment:
              homeTeamInputs.pass_completion_prob_adjustment / 100,
            rush_touchdown_prob_adjustment:
              homeTeamInputs.rush_touchdown_prob_adjustment / 100,
            pass_touchdown_prob_adjustment:
              homeTeamInputs.pass_touchdown_prob_adjustment / 100,
            pass_completion_rate: homeTeamInputs.pass_completion_rate / 100,
            pass_play_prob_adjustment:
              homeTeamInputs.pass_play_prob_adjustment / 100,
            pass_yardage_adjustment: homeTeamInputs.pass_yardage_adjustment,
            rush_yardage_adjustment: homeTeamInputs.rush_yardage_adjustment,
            sack_rate: homeTeamInputs.sack_rate / 100
          },
          {
            prizepicks_team_id: awayTeamInputs.prizepicks_team_id,
            sportradar_team_id: awayTeamInputs.sportradar_team_id,
            team_name: awayTeamInputs.team_name,
            home_team: awayTeamInputs.home_team,
            yards_per_pass_attempt: awayTeamInputs.yards_per_pass_attempt,
            yards_per_rush_attempt: awayTeamInputs.yards_per_rush_attempt,
            pass_attempt_rate: awayTeamInputs.pass_attempt_rate / 100,
            pass_completion_prob_adjustment:
              awayTeamInputs.pass_completion_prob_adjustment / 100,
            rush_touchdown_prob_adjustment:
              awayTeamInputs.rush_touchdown_prob_adjustment / 100,
            pass_touchdown_prob_adjustment:
              awayTeamInputs.pass_touchdown_prob_adjustment / 100,
            pass_completion_rate: awayTeamInputs.pass_completion_rate / 100,
            pass_play_prob_adjustment:
              awayTeamInputs.pass_play_prob_adjustment / 100,
            pass_yardage_adjustment: awayTeamInputs.pass_yardage_adjustment,
            rush_yardage_adjustment: awayTeamInputs.rush_yardage_adjustment,
            sack_rate: awayTeamInputs.sack_rate / 100
          }
        ],
        player_inputs: [
          {
            prizepicks_team_id: homeTeamInputs.prizepicks_team_id,
            sportradar_team_id: homeTeamInputs.sportradar_team_id,
            team_name: homeTeamInputs.team_name,
            home_team: homeTeamInputs.home_team,
            players: homeTeamFinalPlayers
          },
          {
            prizepicks_team_id: awayTeamInputs.prizepicks_team_id,
            sportradar_team_id: awayTeamInputs.sportradar_team_id,
            team_name: awayTeamInputs.team_name,
            home_team: awayTeamInputs.home_team,
            players: awayTeamFinalPlayers
          }
        ],
        removed_players: [
          {
            prizepicks_team_id: homeTeamInputs.prizepicks_team_id,
            sportradar_team_id: homeTeamInputs.sportradar_team_id,
            team_name: homeTeamInputs.team_name,
            home_team: homeTeamInputs.home_team,
            players: homeTeamRemovedPlayers
          },
          {
            prizepicks_team_id: awayTeamInputs.prizepicks_team_id,
            sportradar_team_id: awayTeamInputs.sportradar_team_id,
            team_name: awayTeamInputs.team_name,
            home_team: awayTeamInputs.home_team,
            players: awayTeamRemovedPlayers
          }
        ]
      }
    };

    const requestSimResponse = await requestSimulationRun(payload);

    // bruh don't ask me why this is what it is
    const overrideType = requestSimResponse as unknown as {
      data: {
        data: { id: string };
      };
    };

    const path = `${PATHS.SIMULATIONS}/${leagueId}/${gameId}/${overrideType?.data?.data.id ? String(overrideType?.data?.data.id) : '4'}`;

    navigate(path);

    return requestSimResponse;
  };

  return {
    handleRequestSimulationRun
  };
};
